import { asCompanyId, type CompanyId } from "@repo/app-types/entities/company";
import { isAlpharunEmail } from "@repo/app-types/helpers/utilities";
import { PrivacyCurtain } from "@repo/ui/privacyCurtain/privacyCurtain";
import { usePrivacyCurtain } from "@repo/ui/privacyCurtain/usePrivacyCurtain";
import { IconBuilding } from "@tabler/icons-react";
import { useEffect, useState, type FC } from "react";
import { getWorkspaceAlias } from "../../helpers/workspaceAlias.js";
import { useAppSelector } from "../../hooks/hook.js";
import {
  selectCompany,
  selectIsAlpharunWorkspace,
} from "../company/companySlice.js";
import { selectTeammate } from "../teammate/teammateSlice.js";

/** Customer workspaces the curtain is switched off for. */
const CURTAIN_EXEMPT_COMPANY_IDS: ReadonlySet<CompanyId> = new Set([
  // Acme Financial
  asCompanyId("43e0441a-8a8b-48c0-83ed-3c6c863185d0"),
]);

/** How long the post-reveal workspace confirmation stays on screen. */
const REVEAL_CONFIRMATION_DURATION_MS = 6_000;

interface WorkspaceRevealConfirmationProps {
  companyName: string;
  onDismiss: () => void;
}

/**
 * Prominent, self-dismissing banner shown right after the curtain is lifted,
 * so the employee can confirm they are in the intended customer account.
 * This is the only place the flow shows the real customer name.
 */
const WorkspaceRevealConfirmation: FC<WorkspaceRevealConfirmationProps> = (
  props,
) => {
  const { companyName, onDismiss } = props;

  useEffect(() => {
    const timeoutId = window.setTimeout(
      onDismiss,
      REVEAL_CONFIRMATION_DURATION_MS,
    );
    return () => window.clearTimeout(timeoutId);
  }, [onDismiss]);

  return (
    <div className="pointer-events-none fixed inset-x-0 top-6 z-[9998] flex justify-center">
      <div
        role="status"
        className="pointer-events-auto flex items-center gap-3 rounded-xl bg-slate-950 px-6 py-4 text-white shadow-2xl"
      >
        <IconBuilding aria-hidden className="size-6 shrink-0" />
        <div>
          <div className="text-xs font-medium tracking-wide text-slate-400 uppercase">
            You are viewing
          </div>
          <div className="text-xl font-semibold">{companyName}</div>
        </div>
      </div>
    </div>
  );
};

export interface EmployeePrivacyCurtainProps {
  /** Test-only override of the concealed-time threshold. */
  concealedThresholdMs?: number;
}

/**
 * Screen-share privacy curtain for Alpharun employees viewing a customer
 * workspace: after the tab has been backgrounded past the threshold, an opaque
 * cover replaces the app (and the tab title) until explicitly dismissed, and
 * the curtain never shows the customer's real name.
 */
export const EmployeePrivacyCurtain: FC<EmployeePrivacyCurtainProps> = (
  props,
) => {
  const { concealedThresholdMs } = props;
  const teammate = useAppSelector(selectTeammate);
  const company = useAppSelector(selectCompany);
  const isAlpharunWorkspace = useAppSelector(selectIsAlpharunWorkspace);

  const isEmployeeInCustomerWorkspace =
    isAlpharunEmail(teammate?.email) &&
    company !== null &&
    !isAlpharunWorkspace &&
    !CURTAIN_EXEMPT_COMPANY_IDS.has(company.id);

  // The masked tab title carries the alias too, so the employee can tell
  // covered Alpharun tabs apart in the tab strip without naming the customer.
  const workspaceAlias =
    company === null ? undefined : getWorkspaceAlias(company.id);

  const { isCurtainActive, revealApp } = usePrivacyCurtain({
    isEnabled: isEmployeeInCustomerWorkspace,
    maskedDocumentTitle: `Alpharun | ${workspaceAlias ?? "Customer workspace"}`,
    concealedThresholdMs,
  });

  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);

  if (!isEmployeeInCustomerWorkspace) {
    return null;
  }

  if (isCurtainActive) {
    return (
      <PrivacyCurtain
        workspaceAlias={getWorkspaceAlias(company.id)}
        onReveal={() => {
          revealApp();
          setIsConfirmationVisible(true);
        }}
      />
    );
  }

  if (isConfirmationVisible) {
    return (
      <WorkspaceRevealConfirmation
        companyName={company.name}
        onDismiss={() => setIsConfirmationVisible(false)}
      />
    );
  }

  return null;
};
